/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – SensorPanel.tsx FINAL
// Drawer cu istoric orar vs zilnic real (agregate min/max/avg)
// ============================================================

import React, { useState, useMemo } from 'react';
import { Input, Select, Drawer, message } from 'antd';
import {
  SearchOutlined, DownloadOutlined, ReloadOutlined,
  EnvironmentOutlined, RiseOutlined, FallOutlined,
  ThunderboltOutlined, WifiOutlined, ClockCircleOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

import { SensorData, SensorStatus, SensorHistory } from '../../types';
import { useSimulatorData } from '../../context/SimulatorContext';
import { sensorHistory, mockSensors, mockFarms } from '../../data/mockData';

const { Option } = Select;

// ─── Constante ────────────────────────────────────────────

const SC: Record<SensorStatus, string> = {
  online: '#10b981', warning: '#f59e0b', offline: '#ef4444', calibrating: '#6366f1',
};
const SL: Record<SensorStatus, string> = {
  online: 'Online', warning: 'Avertisment', offline: 'Offline', calibrating: 'Calibrare',
};
const TC: Record<string, string> = {
  'FO-SPR': '#6366f1', 'SAW': '#06b6d4', 'Electrochimic': '#10b981',
};

// ─── Agregare date zilnice ────────────────────────────────

const aggregateDaily = (data: { timestamp: string; value: number }[]) => {
  const byDay: Record<string, number[]> = {};
  data.forEach(p => {
    const day = p.timestamp.slice(0, 10);
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(p.value);
  });
  return Object.entries(byDay).map(([day, vals]) => ({
    time:  new Date(day).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' }),
    avg:   parseFloat((vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(4)),
    min:   parseFloat(Math.min(...vals).toFixed(4)),
    max:   parseFloat(Math.max(...vals).toFixed(4)),
    count: vals.length,
  })).slice(-30);
};

// ─── Drawer senzor ────────────────────────────────────────

const SensorDrawer: React.FC<{ sensor: SensorData | null; onClose: () => void }> = ({ sensor, onClose }) => {
  const { liveHistory } = useSimulatorData();
  const [histMode, setHistMode] = useState<'hourly' | 'daily'>('hourly');

  if (!sensor) return null;

  const lh = liveHistory.find(h => h.sensorId === sensor.id);
  const sh = sensorHistory.find(h => h.sensorId === sensor.id);
  const rawData = (lh?.data ?? sh?.data ?? []);

  const hourlyData = rawData.slice(-48).map(p => ({
    time:  new Date(p.timestamp).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    value: p.value,
  }));

  const dailyData = aggregateDaily(rawData);

  const chartData = histMode === 'hourly' ? hourlyData : dailyData;
  const color     = SC[sensor.status];
  const farm      = mockFarms.find(f => f.id === sensor.farmId);

  const vals   = rawData.slice(-48).map(p => p.value);
  const avg    = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  const trend  = vals.length >= 10
    ? ((vals.slice(-5).reduce((s, v) => s + v, 0) / 5) - (vals.slice(0, 5).reduce((s, v) => s + v, 0) / 5)) / (vals.slice(0, 5).reduce((s, v) => s + v, 0) / 5) * 100
    : 0;

  return (
    <Drawer
      title={null} open={!!sensor} onClose={onClose}
      placement="right" width={480}
      styles={{ body: { padding: 0 }, header: { display: 'none' } }}
    >
      {/* Header */}
      <div style={{ padding: '20px 22px', background: `${color}08`, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color }}>
            <ExperimentOutlined />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{sensor.name}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: `${color}15`, color }}>{SL[sensor.status]}</span>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: `${TC[sensor.type] ?? '#6366f1'}12`, color: TC[sensor.type] ?? '#6366f1' }}>{sensor.type}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 16, color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
      </div>

      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 18, overflowY: 'auto', maxHeight: 'calc(100vh - 130px)' }}>

        {/* Valori curente */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { l: 'Valoare curentă', v: `${sensor.value.toFixed(4)} ${sensor.unit}`, c: sensor.value >= sensor.lod ? '#ef4444' : '#10b981' },
            { l: 'Limita detecție', v: `${sensor.lod} ${sensor.unit}`, c: '#f59e0b' },
            { l: 'Maxim legal UE',  v: `${sensor.maxAllowed} ${sensor.unit}`, c: '#ef4444' },
            { l: 'Medie 48h',       v: `${avg.toFixed(4)} ${sensor.unit}`, c: '#6366f1' },
          ].map((s, i) => (
            <div key={i} style={{ padding: '10px 14px', borderRadius: 10, background: '#f8fafc', border: '1px solid #f1f5f9', textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Hardware */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {[
            { icon: <ThunderboltOutlined />, l: 'Baterie', v: `${sensor.batteryLevel ?? 0}%`, c: (sensor.batteryLevel ?? 0) < 20 ? '#ef4444' : (sensor.batteryLevel ?? 0) < 40 ? '#f59e0b' : '#10b981' },
            { icon: <WifiOutlined />,        l: 'Semnal',  v: `${sensor.signalStrength ?? 0}%`, c: (sensor.signalStrength ?? 0) < 30 ? '#ef4444' : '#10b981' },
            { icon: <ClockCircleOutlined />, l: 'Trend 48h', v: `${trend >= 0 ? '+' : ''}${trend.toFixed(1)}%`, c: trend > 5 ? '#ef4444' : trend < -5 ? '#10b981' : '#94a3b8' },
          ].map((h, i) => (
            <div key={i} style={{ padding: '10px', borderRadius: 10, background: '#f8fafc', border: '1px solid #f1f5f9', textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: h.c, marginBottom: 4 }}>{h.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: h.c }}>{h.v}</div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>{h.l}</div>
            </div>
          ))}
        </div>

        {/* Grafic istoric */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Istoric</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['hourly', 'daily'] as const).map(m => (
                <button key={m} onClick={() => setHistMode(m)} style={{ padding: '4px 12px', borderRadius: 7, fontSize: 11, fontWeight: histMode === m ? 700 : 500, border: `1px solid ${histMode === m ? color : '#e2e8f0'}`, background: histMode === m ? `${color}15` : '#fff', color: histMode === m ? color : '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
                  {m === 'hourly' ? 'Orar (48h)' : 'Zilnic (30z)'}
                </button>
              ))}
            </div>
          </div>

          <ResponsiveContainer width="100%" height={200}>
            {histMode === 'hourly' ? (
              <AreaChart data={hourlyData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <defs>
                  <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={color} stopOpacity={0.18} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="time" tick={{ fontSize: 8, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={7} />
                <YAxis tick={{ fontSize: 8, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11 }} formatter={(v: any) => [`${v} ${sensor.unit}`]} />
                <ReferenceLine y={sensor.lod}        stroke="#f59e0b" strokeDasharray="4 2" label={{ value: 'LOD', position: 'insideTopRight', fontSize: 8, fill: '#f59e0b' }} />
                <ReferenceLine y={sensor.maxAllowed} stroke="#ef4444" strokeDasharray="4 2" label={{ value: 'MAX', position: 'insideTopRight', fontSize: 8, fill: '#ef4444' }} />
                <Area type="monotone" dataKey="value" stroke={color} fill="url(#aGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            ) : (
              <BarChart data={dailyData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="time" tick={{ fontSize: 8, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={4} />
                <YAxis tick={{ fontSize: 8, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11 }} formatter={(v: any, n: any) => [`${v} ${sensor.unit}`, n === 'avg' ? 'Medie' : n === 'max' ? 'Max' : 'Min']} />
                <ReferenceLine y={sensor.lod}        stroke="#f59e0b" strokeDasharray="4 2" />
                <ReferenceLine y={sensor.maxAllowed} stroke="#ef4444" strokeDasharray="4 2" />
                <Bar dataKey="min"  name="min"  fill={`${color}30`} radius={[2,2,0,0]} />
                <Bar dataKey="avg"  name="avg"  fill={color}        radius={[2,2,0,0]} />
                <Bar dataKey="max"  name="max"  fill={`${color}80`} radius={[2,2,0,0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
          <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', marginTop: 4 }}>
            {histMode === 'hourly' ? 'Puncte orare · Ultimele 48h' : 'Agregate zilnice (min/medie/max) · Ultimele 30 zile'}
          </div>
        </div>

        {/* Info locație */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Informații</div>
          {[
            ['Locație',    sensor.location.label],
            ['Fermă',      sensor.farmName ?? '–'],
            ['Tip senzor', sensor.type],
            ['Parametru',  sensor.parameter],
            ['Coord GPS',  `${sensor.location.lat.toFixed(4)}, ${sensor.location.lng.toFixed(4)}`],
            ['Timestamp',  new Date(sensor.timestamp).toLocaleString('ro-RO')],
            ...(farm ? [['Tip fermă', farm.type], ['Proprietar', farm.ownerName], ['Suprafață', `${farm.surface} ha`]] : []),
          ].map(([k, v], i) => (
            <div key={i} style={{ display: 'flex', padding: '7px 0', borderBottom: '1px solid #f8fafc' }}>
              <span style={{ width: '40%', fontSize: 11, color: '#94a3b8' }}>{k}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#0f172a', flex: 1 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
};

// ─── Sensor Row ───────────────────────────────────────────

const SensorRow: React.FC<{ sensor: SensorData; liveHistory: SensorHistory[]; onClick: () => void }> = ({ sensor: s, liveHistory, onClick }) => {
  const lh    = liveHistory.find(h => h.sensorId === s.id);
  const vals  = (lh?.data ?? []).slice(-10).map(p => p.value);
  const prev  = vals[vals.length - 2] ?? s.value;
  const color = SC[s.status];
  const pct   = Math.round((s.value / s.lod) * 100);
  const bat   = s.batteryLevel ?? 50;
  const trendDir = s.value > prev * 1.02 ? 'up' : s.value < prev * 0.98 ? 'down' : 'stable';
  const trendPct = prev > 0 ? Math.abs(((s.value - prev) / prev) * 100) : 0;

  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr 1.2fr 0.8fr 0.8fr 1fr',
        gap: 8, padding: '12px 16px', cursor: 'pointer',
        borderBottom: '1px solid #f8fafc', transition: 'background 0.15s',
        alignItems: 'center',
      }}
      onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = '#fafbff'}
      onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
    >
      {/* Senzor */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{s.name}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <EnvironmentOutlined style={{ fontSize: 9 }} /> {s.location.label}
        </div>
      </div>

      {/* Tip */}
      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: `${TC[s.type] ?? '#6366f1'}12`, color: TC[s.type] ?? '#6366f1' }}>{s.type}</span>

      {/* Parametru + valoare */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: s.value >= s.lod ? '#ef4444' : '#0f172a' }}>{s.value.toFixed(4)} {s.unit}</div>
        <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.parameter}</div>
        <div style={{ marginTop: 4, height: 4, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden', width: 80 }}>
          <div style={{ height: '100%', borderRadius: 99, width: `${Math.min(pct, 100)}%`, background: pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981' }} />
        </div>
      </div>

      {/* LOD % */}
      <div style={{ fontSize: 12, color: '#64748b' }}>
        <span style={{ fontWeight: 700, color: pct >= 100 ? '#ef4444' : '#475569' }}>{pct}%</span>
        <div style={{ fontSize: 10, color: '#94a3b8' }}>LOD: {s.lod}</div>
      </div>

      {/* Trend */}
      <div style={{ fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, color: trendDir === 'up' ? '#ef4444' : trendDir === 'down' ? '#10b981' : '#94a3b8' }}>
        {trendDir === 'up' ? <RiseOutlined /> : trendDir === 'down' ? <FallOutlined /> : '–'}
        {trendDir !== 'stable' && `${trendPct.toFixed(1)}%`}
      </div>

      {/* Baterie */}
      <div style={{ fontSize: 12, fontWeight: 700, color: bat < 20 ? '#ef4444' : bat < 40 ? '#f59e0b' : '#10b981' }}>{bat.toFixed(0)}%</div>

      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0, ...(s.status === 'warning' ? { boxShadow: `0 0 0 3px ${color}30` } : {}) }} />
        <span style={{ fontSize: 11, color: '#64748b' }}>{SL[s.status]}</span>
      </div>
    </div>
  );
};

// ─── Panel principal ──────────────────────────────────────

const SensorPanel: React.FC = () => {
  const { sensors, liveHistory } = useSimulatorData();
  const [search,   setSearch]   = useState('');
  const [statusF,  setStatusF]  = useState('all');
  const [typeF,    setTypeF]    = useState('all');
  const [farmF,    setFarmF]    = useState('all');
  const [selected, setSelected] = useState<SensorData | null>(null);

  const farms = useMemo(() =>
    Array.from(new Set(sensors.map(s => s.farmName).filter(Boolean) as string[])),
    [sensors]
  );

  const filtered = useMemo(() => sensors.filter(s => {
    const ms = search === '' ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.parameter.toLowerCase().includes(search.toLowerCase()) ||
      s.location.label.toLowerCase().includes(search.toLowerCase());
    return ms && (statusF === 'all' || s.status === statusF) && (typeF === 'all' || s.type === typeF) && (farmF === 'all' || s.farmName === farmF);
  }), [sensors, search, statusF, typeF, farmF]);

  const counts = (Object.keys(SL) as SensorStatus[]).map(k => ({
    key: k, label: SL[k], color: SC[k],
    count: sensors.filter(s => s.status === k).length,
  }));

  const exportCSV = () => {
    const header = 'Senzor,Tip,Parametru,Valoare,Unitate,LOD,Max Admis,Status,Locatie,Ferma,Baterie%,Semnal%,Timestamp';
    const rows = filtered.map(s =>
      `${s.name},${s.type},${s.parameter},${s.value.toFixed(4)},${s.unit},${s.lod},${s.maxAllowed},${s.status},"${s.location.label}","${s.farmName ?? ''}",${(s.batteryLevel ?? 0).toFixed(0)},${(s.signalStrength ?? 0).toFixed(0)},${s.timestamp}`
    );
    const csv  = [header, ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `senzori_SIMADCA_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    message.success(`${filtered.length} senzori exportați CSV.`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>Senzori</h2>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{filtered.length} din {sensors.length} senzori · date live · click pentru detalii</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {counts.map(c => (
            <button key={c.key} onClick={() => setStatusF(statusF === c.key ? 'all' : c.key)} style={{ padding: '5px 12px', borderRadius: 99, cursor: 'pointer', border: `1.5px solid ${statusF === c.key ? c.color : `${c.color}30`}`, background: statusF === c.key ? `${c.color}15` : '#fff', display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', transition: 'all 0.15s' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.color, display: 'inline-block' }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: c.color }}>{c.count}</span>
              <span style={{ fontSize: 11, color: '#64748b' }}>{c.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Filtre */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder="Caută senzor, parametru, locație…"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={search} onChange={e => setSearch(e.target.value)}
          allowClear style={{ flex: 1, minWidth: 200 }}
        />
        <Select value={statusF} onChange={setStatusF} style={{ width: 155 }}>
          <Option value="all">Toate statusurile</Option>
          {(Object.entries(SL) as [SensorStatus, string][]).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
        </Select>
        <Select value={typeF} onChange={setTypeF} style={{ width: 145 }}>
          <Option value="all">Toate tipurile</Option>
          {['FO-SPR','SAW','Electrochimic'].map(t => <Option key={t} value={t}>{t}</Option>)}
        </Select>
        <Select value={farmF} onChange={setFarmF} style={{ width: 200 }}>
          <Option value="all">Toate fermele</Option>
          {farms.map(f => <Option key={f} value={f}>{f.split('–')[0].trim()}</Option>)}
        </Select>
        <button onClick={() => { setSearch(''); setStatusF('all'); setTypeF('all'); setFarmF('all'); }} style={{ padding: '7px 12px', borderRadius: 8, fontSize: 11, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5 }}>
          <ReloadOutlined /> Reset
        </button>
        <button onClick={exportCSV} style={{ padding: '7px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#059669', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}>
          <DownloadOutlined /> Export CSV
        </button>
      </div>

      {/* Tabel */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr 1.2fr 0.8fr 0.8fr 1fr', gap: 8, padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
          {['Senzor', 'Tip', 'Parametru / Valoare', '% LOD', 'Trend', 'Baterie', 'Status'].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>{h.toUpperCase()}</div>
          ))}
        </div>
        {filtered.map(s => (
          <SensorRow key={s.id} sensor={s} liveHistory={liveHistory} onClick={() => setSelected(s)} />
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
            <SearchOutlined style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
            <div style={{ fontSize: 13, fontWeight: 600 }}>Niciun senzor găsit</div>
          </div>
        )}
      </div>

      <SensorDrawer sensor={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default SensorPanel;